import { computed } from 'vue';
import { useStore } from 'vuex';
import { useDashboard } from '@rdrive/plugin-dashboard-shell';
import { useAnalytics } from '@/utils/useAnalytics';

/**
 * THE bridge module pages read app context through, so they repeat neither eight
 * `computed(() => store.getters.x)` lines nor the platform wiring. Base components never call
 * it — they take props.
 *
 * Two sources since IA_ABSORPTION_PLAN 6b-2b:
 *  - the PLATFORM half — the DuckDB connection, the REST client, the data loader, the URL
 *    settings, `dataLoaded`, the filter contexts, analytics — from the dashboard
 *    (`useDashboard()`, @rdrive/plugin-dashboard-shell);
 *  - the DOMAIN half — the lookup objects, `showOpenItemsOnly`, the theme's chart font colour,
 *    the logo, build mode — from the Vuex store. This is still the only composable allowed to
 *    call `useStore()`, so when Vuex is retired only this file changes.
 *
 * Semantics preserved from the call sites it replaced:
 *  - `conn`, `db`, `apiClient` and `loader` are NOT computed. Pages read them once at
 *    setup, as they always did (`const conn = store.getters.getDBConnection`). They are
 *    stable objects, and adapters unref whatever they are given. `loader` is the
 *    dashboard's one data loader (@rdrive/data-loader): a page loads a lazy manifest
 *    table with `loader.ensure(name)`.
 *  - `apiHost` is `settings.apiUrl` with a leading `api-` stripped — the form
 *    every deep-link in the templates builds by hand.
 *  - `dataLoaded` is the GLOBAL flag (the loader's). Pages with their own load lifecycle
 *    (ActivityPage's lazily-loaded parquet) keep their local ref and must not use this one.
 *  - the filter contexts are computeds over the dashboard's `filters`, never captured values,
 *    so a replaced context (the latent replacement path) reaches every reader.
 */
export function useAppContext() {
  const store = useStore();
  const dashboard = useDashboard();
  const { trackEvent } = useAnalytics();

  const settings = computed(() => dashboard.settings);

  const apiHost = computed(() => {
    const url = dashboard.settings.apiUrl || '';
    return url.startsWith('api-') ? url.slice(4) : url;
  });

  return {
    // Stable singletons — read once, like the pages already do
    conn: dashboard.conn,
    db: dashboard.duckdb?.db,
    apiClient: dashboard.api,
    loader: dashboard.loader,

    // Reactive values — platform
    settings,
    userId: computed(() => dashboard.settings.userId),
    projectCode: computed(() => dashboard.settings.projectCode),
    projectName: computed(() => dashboard.settings.projectName),
    apiHost,
    dataLoaded: computed(() => dashboard.dataLoaded.value),

    // Reactive values — domain (the store)
    logoUrl: computed(() => store.getters.getLogoUrl),
    chartFontColor: computed(() => store.getters.chartFontColor),
    statusTemplates: computed(() => store.getters.getStatusTemplatesObj),
    itemTemplates: computed(() => store.getters.getItemTemplatesObj),
    categories: computed(() => store.getters.getCategoriesObj),
    showOpenItemsOnly: computed(() => store.getters.getShowOpenItemsOnly),
    buildMode: computed(() => store.state.buildMode),

    // The dashboard's filter contexts
    filterItemsContext: computed(() => dashboard.filters.items),
    filterStatusHistoryContext: computed(() => dashboard.filters.statusHistory),

    trackEvent
  };
}
